import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import { Colors } from '../utils/colors';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@mui/material';
import logo from '../assets/logofl.jpg';
import { useDispatch, useSelector } from 'react-redux';
import { setAccessToken, setIsAuthenticated, setUser } from '../Redux/Reducers/appState';
import { AuthService } from '../services/auth/Auth';
import { broadcastUserLogout } from '../utils/userSessionTimeout';

const pages = ['Results', 'Live Draws', 'Buy Ticket', 'Winners'];

const getDisplayName = (user) => {
  if (!user) return "";

  const firstName = String(user.first_name || "").trim();
  const lastName = String(user.last_name || "").trim();
  const lastInitial = lastName ? `${lastName.charAt(0).toUpperCase()}.` : "";

  return [firstName, lastInitial].filter(Boolean).join(" ");
};

const ResponsiveAppBar = () => {
  const headerRef = React.useRef(null);

  React.useLayoutEffect(() => {
    const header = headerRef.current;
    const updateHeight = () => document.documentElement.style.setProperty(
      "--app-header-height", `${header.getBoundingClientRect().height}px`
    );
    updateHeight();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateHeight) : null;
    observer?.observe(header);
    window.addEventListener("resize", updateHeight);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateHeight);
      document.documentElement.style.removeProperty("--app-header-height");
    };
  }, []);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const sessionExpired = useSelector((state) => state.appState.sessionExpired);
  const user = useSelector((state) => state.appState.user);
  const isAuthenticated = useSelector((data)=>data.appState.isAuthenticated)
  const branding = useSelector((state) => state.appState.branding);
  const logoUrl = branding?.logoUrl || "";
  const logoDimensions = {
    width: Number(branding?.logoWidth) || 96,
    height: Number(branding?.logoHeight) || 96
  };
  const showFallbackLogo = Boolean(branding?.loaded && !logoUrl);
  const displayName = getDisplayName(user);

  const handleLogout = async () => {
    try {
      await AuthService.logout({
        id: null,
        email: null,
        ipaddress: null
      });
    } catch (error) {
      console.log(error);
    }

    dispatch(setUser(null));
    dispatch(setAccessToken(null));
    dispatch(setIsAuthenticated(false));
    broadcastUserLogout({ reason: "manual" });
    navigate("/login");
  };

  const handleRoute = (page) => () => {
    if(page === 'Results'){
        navigate(`/winners`);
    }else if(page === 'Live Draws'){
        navigate(`/draw`);
    }else if(page === 'Buy Ticket'){
        navigate(`/buy`);
    }else if(page === 'Winners'){
        navigate(`/winners`);
    }
  }

  return (
    <AppBar ref={headerRef} position="fixed" sx={{ marginBottom:8 }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {!isAuthenticated ? (
            <Box
              sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: "1fr auto 1fr",
                alignItems: "center",
                gap: 1
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <Button
                  onClick={() => navigate("/register")}
                  sx={{ color: "#fff", fontWeight: 700 }}
                >
                  Register
                </Button>
              </Box>

              <Box
                component={logoUrl || showFallbackLogo ? "img" : "div"}
                src={logoUrl || (showFallbackLogo ? logo : undefined)}
                alt="Fliplottery Logo"
                sx={{
                  width: { xs: Math.max(56, Math.round(logoDimensions.width * 0.8)), md: logoDimensions.width },
                  height: { xs: Math.max(56, Math.round(logoDimensions.height * 0.8)), md: logoDimensions.height },
                  objectFit: "contain"
                }}
              />

              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  onClick={() => navigate("/login")}
                  sx={{ color: "#fff", fontWeight: 700 }}
                >
                  Login
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexGrow: 1
                }}
              >
                <Box
                  component={logoUrl || showFallbackLogo ? "img" : "div"}
                  src={logoUrl || (showFallbackLogo ? logo : undefined)}
                  alt="Fliplottery Logo"
                  sx={{
                    width: { xs: Math.max(56, Math.round(logoDimensions.width * 0.8)), md: logoDimensions.width },
                    height: { xs: Math.max(56, Math.round(logoDimensions.height * 0.8)), md: logoDimensions.height },
                    objectFit: "contain"
                  }}
                />
              </Box>
              <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
                {pages.map((page) => (
                  <Button
                    key={page}
                    onClick={handleRoute(page)}
                    sx={{ my: 2, color: 'white', display: 'block' }}
                  >

                  </Button>
                ))}
              </Box>
              <Box
                sx={{
                  flexGrow: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  gap: 0.25
                }}
              >
                <Button
                  color="inherit"
                  onClick={handleLogout}
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    minWidth: "auto",
                    px: 1,
                    lineHeight: 1
                  }}
                >
                  Logout
                </Button>

                {displayName ? (
                  <Typography
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: { xs: 11, sm: 13 },
                      maxWidth: { xs: 110, sm: 160 },
                      textAlign: "right",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    {displayName}
                  </Typography>
                ) : null}
              </Box>
            </>
          )}
        </Toolbar>
           {(sessionExpired && isAuthenticated) && (
              <Alert
                severity="warning"
                sx={{ mb: 2, fontWeight: 600 }}
              >
                Your login session has expired. Please log in again.
              </Alert>
            )}
      </Container>

         
      
           
    </AppBar>
  );
}
export default ResponsiveAppBar;
